/**
 * ergonomics.ts - Parametry ergonomiczne dla ekspozytorów POS
 * PlexiSystem Engineering Module
 */

export interface ErgonomicDimensions {
  width?: [number, number];
  height?: [number, number];
  width_min?: number;
  height_min?: number;
  max?: number;
}

export const ERGO = {
  // Otwory na dłonie
  hand_opening_mm: {
    width: [120, 170],
    height: [150, 220]
  },
  
  // Otwory dla dwóch rąk
  two_hands_opening_mm: {
    width_min: 180,
    height_min: 220
  },
  
  // Szczeliny na palce
  slots_fingers_mm: {
    width_min: 20
  },
  
  // Kąty nachylenia półek
  shelf_angle_deg: {
    floor: [15, 25],      // Ekspozytory podłogowe
    leaflets: [20, 30],   // Na ulotki
    counter_max: 30       // Blatowe maksymalnie
  },
  
  // Wysokości standardowe [cm]
  heights_cm: {
    fsdu: [150, 165],          // Free Standing Display Unit
    infoStand: [130, 150],     // Stojaki informacyjne
    counter: [20, 40],         // Ekspozytory blatowe
    shelf_standard: [180, 200] // Standardowe regały
  },
  
  // Złota strefa widoczności [cm od podłogi]
  golden_zone_cm: [120, 160],
  
  // Strefy widoczności
  visibility_zones: {
    eye_level: [140, 170],    // Poziom oczu
    stretch: [170, 200],      // Wyciągnięta ręka
    stoop: [60, 120],        // Pochylenie
    floor: [0, 60]           // Poziom podłogi
  },
  
  // Minimalne odstępy i prześwity
  clearances_mm: {
    between_products: 50,     // Między produktami
    from_edge: 30,           // Od krawędzi
    finger_space: 25,        // Przestrzeń na palce
    hand_clearance: 100      // Luz dla dłoni
  },
  
  // Standardowe wymiary produktów do ekspozycji
  product_sizes_mm: {
    brochure_a4: [210, 297],
    brochure_a5: [148, 210],
    brochure_dl: [99, 210],
    business_card: [85, 55],
    cd_case: [124, 142],
    book_standard: [150, 230]
  }
};

/**
 * Sprawdza ergonomię otworów
 */
export function validateOpenings(
  width_mm: number,
  height_mm: number,
  type: 'single_hand' | 'two_hands'
): { valid: boolean; warnings: string[] } {
  const warnings: string[] = [];
  let valid = true;
  
  if (type === 'single_hand') {
    if (width_mm < ERGO.hand_opening_mm.width[0]) {
      warnings.push(`Szerokość otworu ${width_mm}mm za mała dla dłoni (min. ${ERGO.hand_opening_mm.width[0]}mm)`);
      valid = false;
    }
    if (height_mm < ERGO.hand_opening_mm.height[0]) {
      warnings.push(`Wysokość otworu ${height_mm}mm za mała dla dłoni (min. ${ERGO.hand_opening_mm.height[0]}mm)`);
      valid = false;
    }
  } else {
    if (width_mm < ERGO.two_hands_opening_mm.width_min) {
      warnings.push(`Szerokość otworu ${width_mm}mm za mała dla dwóch rąk (min. ${ERGO.two_hands_opening_mm.width_min}mm)`);
      valid = false;
    }
    if (height_mm < ERGO.two_hands_opening_mm.height_min) {
      warnings.push(`Wysokość otworu ${height_mm}mm za mała dla dwóch rąk (min. ${ERGO.two_hands_opening_mm.height_min}mm)`);
      valid = false;
    }
  }
  
  return { valid, warnings };
}

/**
 * Oblicza optymalną wysokość półki
 */
export function calculateOptimalShelfHeight(
  productImportance: 'high' | 'medium' | 'low',
  displayType: 'fsdu' | 'counter' | 'shelf'
): { height_cm: number; zone: string } {
  if (displayType === 'counter') {
    return {
      height_cm: 30,
      zone: 'Counter level'
    };
  }
  
  if (productImportance === 'high') {
    // Złota strefa
    return {
      height_cm: 140,
      zone: 'Golden zone (eye level)'
    };
  } else if (productImportance === 'medium') {
    // Strefa wyciągniętej ręki lub pochylenia
    return {
      height_cm: 100,
      zone: 'Stoop zone'
    };
  } else {
    // Strefa podłogowa
    return {
      height_cm: 40,
      zone: 'Floor zone'
    };
  }
}

/**
 * Oblicza liczbę półek dla danej wysokości
 */
export function calculateShelfCount(
  totalHeight_cm: number,
  productHeight_cm: number,
  includeGoldenZone: boolean = true
): { count: number; spacing_cm: number; goldenZoneShelf?: number } {
  const minSpacing = productHeight_cm + 5; // 5cm luzu
  const maxShelves = Math.floor(totalHeight_cm / minSpacing);
  
  let goldenZoneShelf: number | undefined;
  
  if (includeGoldenZone) {
    // Znajdź która półka będzie w golden zone
    const goldenHeight = (ERGO.golden_zone_cm[0] + ERGO.golden_zone_cm[1]) / 2;
    goldenZoneShelf = Math.round(goldenHeight / minSpacing);
  }
  
  return {
    count: maxShelves,
    spacing_cm: totalHeight_cm / maxShelves,
    goldenZoneShelf
  };
}

/**
 * Sprawdza widoczność produktu na danej wysokości
 */
export function checkVisibility(height_cm: number): {
  zone: string;
  visibility: 'excellent' | 'good' | 'fair' | 'poor';
  recommendation?: string;
} {
  if (height_cm >= ERGO.golden_zone_cm[0] && height_cm <= ERGO.golden_zone_cm[1]) {
    return {
      zone: 'Golden Zone',
      visibility: 'excellent',
      recommendation: 'Idealna wysokość dla kluczowych produktów'
    };
  }
  
  if (height_cm >= ERGO.visibility_zones.eye_level[0] && height_cm <= ERGO.visibility_zones.eye_level[1]) {
    return {
      zone: 'Eye Level',
      visibility: 'excellent',
      recommendation: 'Doskonała widoczność'
    };
  }
  
  if (height_cm >= ERGO.visibility_zones.stoop[0] && height_cm <= ERGO.visibility_zones.stoop[1]) {
    return {
      zone: 'Stoop Zone',
      visibility: 'good',
      recommendation: 'Wymaga pochylenia się'
    };
  }
  
  if (height_cm >= ERGO.visibility_zones.stretch[0] && height_cm <= ERGO.visibility_zones.stretch[1]) {
    return {
      zone: 'Stretch Zone',
      visibility: 'fair',
      recommendation: 'Wymaga wyciągnięcia ręki'
    };
  }
  
  return {
    zone: 'Floor Zone',
    visibility: 'poor',
    recommendation: 'Słaba widoczność, odpowiednie dla produktów o niskim priorytecie'
  };
}
